import { z } from 'zod';
import { PLAIN_NAME_MESSAGE, plainName } from './json.js';
import { CUSTOM_ROLE_KEY, isBuiltInRole, KIND_ROLES } from './rbac.js';
/**
 * People in a tenant (task 52, pilot onboarding). A tenant admin invites someone by email with roles; the
 * membership is created when that person first signs in with the same email, verified by the identity
 * provider. Admins change roles and suspend people; a tenant always keeps one active admin.
 */
/** How long an invitation can be accepted. */
export const INVITE_DAYS = 14;
/** Built-in roles each kind of tenant hands out (task 78 keeps them in rbac.ts). */
export const ASSIGNABLE_ROLES = KIND_ROLES;
export const ROLE_LABELS = {
    tenant_admin: 'Administrator',
    config_editor: 'Configuration editor',
    config_approver: 'Configuration approver',
    underwriter: 'Underwriter',
    broker: 'Broker',
    sme_owner: 'Business owner',
    sme_member: 'Business member',
    auditor: 'Auditor',
};
/** Built-in role names, or a tenant's own role keys (task 78). */
const RoleKey = z.string().refine((k) => isBuiltInRole(k) || CUSTOM_ROLE_KEY.test(k), 'Not a role');
const Roles = z
    .array(RoleKey)
    .min(1, 'Give at least one role')
    .refine((r) => new Set(r).size === r.length, 'A role is listed twice');
/** What each role is for, in a sentence, where roles are handed out. */
export const ROLE_DESCRIPTIONS = {
    tenant_admin: 'Invites people, changes their roles and settings, and sees everything in the organisation.',
    config_editor: 'Drafts questions, rules and journeys in the studio, and sees clients’ scores read-only.',
    config_approver: 'Approves configuration releases someone else drafted, and sees clients’ scores read-only.',
    underwriter: 'Starts assessments, reviews answers and evidence, and decides on cover.',
    broker: 'Starts assessments for the businesses they place, and follows them.',
    sme_owner: 'Answers for the business, shares its score and finishes its checks.',
    sme_member: 'Helps answer the Cyber Check and uploads evidence.',
    auditor: 'Reads everything, including the audit trail and the evidence pack, and changes nothing.',
};
/** Platform staff set up an insurer or broker and invite its first administrator (task 58). */
export const ProvisionTenant = z.strictObject({
    name: z.string().trim().min(2, 'Enter the organisation’s name').max(200, 'Use at most 200 characters').refine(plainName, PLAIN_NAME_MESSAGE),
    kind: z.enum(['insurer', 'broker']),
    country: z.literal('MY').default('MY'),
    admin: z.string().trim().toLowerCase().pipe(z.email('Enter the administrator’s email address')).pipe(z.string().max(320)),
}); // prettier-ignore
export const InviteMember = z.strictObject({
    email: z.string().trim().toLowerCase().pipe(z.email('Enter an email address')).pipe(z.string().max(320)),
    roles: Roles,
});
export const UpdateMember = z
    .strictObject({ roles: Roles.optional(), status: z.enum(['active', 'suspended']).optional() })
    .refine((u) => u.roles || u.status, 'Change the roles or the status');
/** Refuses a change that would leave the tenant with no active administrator. */
export function lastAdminProblem(members, userId, change) {
    // prettier-ignore
    const after = members.map((m) => m.user_id === userId ? { ...m, roles: change.roles ?? m.roles, status: change.status ?? m.status } : m);
    const admins = (ms) => ms.filter((m) => m.status === 'active' && m.roles.includes('tenant_admin')).length; // prettier-ignore
    return admins(members) > 0 && admins(after) === 0
        ? 'This would leave nobody able to administer the tenant. Make someone else an administrator first.'
        : null;
}
/** Built-in roles this tenant may not hand out, if any. Its own roles are checked against what it has. */
export function unassignable(kind, roles) {
    const allowed = ASSIGNABLE_ROLES[kind] ?? [];
    return roles.filter((r) => isBuiltInRole(r) && !allowed.includes(r));
}
//# sourceMappingURL=members.js.map